<script setup>
import SidebarNavigationItem from "./sidebarNavigationItem.vue";
import SidebarNavigationHeading from "./sidebarNavigationHeading.vue";

import {
    CogIcon,
    HomeIcon,
    MenuAlt3Icon,
    PlusSmIcon,
    XIcon,
    AcademicCapIcon,
    SparklesIcon,
    GiftIcon,
    BellIcon,
    UserGroupIcon,
    AdjustmentsIcon,
    TrendingUpIcon,
    GlobeIcon,
    VideoCameraIcon,
    CashIcon,
    ViewGridAddIcon,
    UsersIcon,
    LightBulbIcon,
    RssIcon,
    UserAddIcon,
    CubeTransparentIcon,
    MapIcon,
    PresentationChartBarIcon,
    FolderIcon,
    StatusOnlineIcon,
    StarIcon,
    InboxIcon
} from '@heroicons/vue/outline'
import {computed, onMounted, reactive, ref, watch} from "vue";

const icons = {
    CogIcon,
    HomeIcon,
    MenuAlt3Icon,
    PlusSmIcon,
    XIcon,
    AcademicCapIcon,
    SparklesIcon,
    GiftIcon,
    BellIcon,
    UserGroupIcon,
    AdjustmentsIcon,
    TrendingUpIcon,
    GlobeIcon,
    VideoCameraIcon,
    CashIcon,
    ViewGridAddIcon,
    UsersIcon,
    LightBulbIcon,
    RssIcon,
    UserAddIcon,
    CubeTransparentIcon,
    MapIcon,
    PresentationChartBarIcon,
    FolderIcon,
    StatusOnlineIcon,
    StarIcon,
    InboxIcon
}

const props = defineProps({
    items: {
        type: Array,
        required: true
    },
    hiddenItems: {
        type: Array,
        required: false,
        default: []
    }
})

const notifications = reactive({
    dailyAssistant: 0,
    setupAssistant: 0,
})

const expandedSections = ref([]);
const dashboardNavigationItem = {
    'name': 'navigation:backOffice.sidebar.dashboard.title',
    'href': route('boIndex'),
    'icon': 'fa-gauge-high',
    'hasSubnav': false,
    'itemName': 'dashboard',
    'routeName': 'boIndex',
    'activeRoutes': [ 'boIndex' ],
    'notificationsCount': false
}

const loadNotificationCounts = async () => {
    const res = await axios.get(route('notifications.sidebar'))
    res.data.sections.forEach(section => {
        notifications[section.name] = section.count
    })
}

const toggleSection = (item) => {
    if (expandedSections.value.includes(item.name)) {
        expandedSections.value = expandedSections.value.filter(section => section !== item.name)
    } else {
        expandedSections.value.push(item.name)
        localStorage.setItem('expandedSections', JSON.stringify([item.name]));
    }

}

const filteredItems = computed(() => {
    let shownItems = [];

    props.items.forEach(item => {
        if (item.type !== 'heading' && !props.hiddenItems.items.includes(item.name)) {
            shownItems.push(item);
        }
        if (item.type === 'heading' && !props.hiddenItems.headers.includes(item.name)) {
            shownItems.push(item);
        }
    });

    return shownItems
})

const hierarchialNavigation = computed(() => {
    const hierarchy = [];
    let currentSection = null;

    filteredItems.value.forEach(item => {
        if (item.type === 'heading') {
            currentSection = {
                ...item,
                subitems: []
            };
            hierarchy.push(currentSection);
        } else if (currentSection) {
            currentSection.subitems.push(item);
        } else {
            // Top level items that don't belong to any section
            hierarchy.push(item);
        }
    });

    return hierarchy;
})

onMounted(() => {
    loadNotificationCounts()
    const storedSections = localStorage.getItem('expandedSections');
    if (storedSections) {
        const sections = JSON.parse(storedSections);
        sections.length > 0 ? expandedSections.value = [sections[sections.length - 1]] : []
    }
})

</script>
<template>
    <div class="pr-4 md:pr-0">
        <div class="pt-2 mb-4 shadow-xl bg-wlPrimary rounded-r-wl">
            <div class="">
                <slot name="top" />
            </div>
            <div class="py-2 transform translate-y-px bg-white rounded-r-wl">
                <ul class="py-1 space-y-2">
                    <a
                        :href="dashboardNavigationItem.href"
                        class="block"
                    >
                        <SidebarNavigationHeading
                            :item="dashboardNavigationItem"
                            :is-first="true"
                            :is-last="false"
                            :icon="HomeIcon"
                            :is-expandable="false"
                            :notification-count="false"
                        />
                    </a>
                </ul>
                <ul class="pt-1 pb-3 space-y-2">
                    <li
                        v-for="(item, i) in hierarchialNavigation"
                        :key="i"
                    >
                        <SidebarNavigationHeading
                            :item="item"
                            :is-last="i === items.length - 1"
                            :icon="icons[item.icon]"
                            :is-expanded="expandedSections.includes(item.name)"
                            @click="toggleSection(item)"
                            class="cursor-pointer hover:opacity-50 transition-opacity duration-200 ease-in-out"
                        />

                        <ul
                            class="pl-6 mt-2"
                            v-if="expandedSections.includes(item.name) && item.subitems && item.subitems.length > 0"
                        >
                            <li
                                v-for="(subitem, si) in item.subitems"
                                :key="si"
                                class="mb-1.5"
                            >
                                <SidebarNavigationItem
                                    :item="subitem"
                                    :is-first="si === 0"
                                    :is-last="si === item.subitems.length - 1"
                                    :icon="icons[subitem.icon]"
                                    :notification-count="notifications[subitem.itemName]"
                                />
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </div>
        <div class="flex flex-col items-center justify-between w-54 py-0 pt-8 mb-3 ml-2 bg-white rounded-wl md:ml-0 md:rounded-none md:bg-transparent md:pt-0 lg:px-4">
            <small class="text-center">
                <!--            <a :href="route('changelog.index')">Versionshistorik</a><br>-->
                <a :href="route('terms.general')" class="mf-link">{{ _mft('navigation:backOffice.sidebar.termsAndConditions') }}</a><br>
            </small>
            <div class="px-4 pt-4">
                <improvement-panel />
            </div>
        </div>
    </div>
</template>
